/**
 * AgentMessages — the shared presentation of an Argo conversation.
 *
 * MessageBubble + ActionCard moved verbatim out of ChatView (2026-07-02) so
 * the docked Ask-Argo drawer and the full Chat view render proposals, diffs,
 * autonomy promotion chips, and undo affordances IDENTICALLY. WHY: the
 * confirm card is the product's trust primitive — if the drawer's card
 * looked or behaved differently from the chat's, the operator would have to
 * re-learn what "Confirm" means per surface. One component, one contract.
 */

import { useState } from 'react';
import { Bot, Check, ChevronDown, ListChecks, Lock, RotateCcw, User, X } from 'lucide-react';
import { GlassPanel } from './GlassPanel';
import { C } from '../lib/tokens';
import type { BrainFact, Person } from '../lib/db/types';
import type { ProposedAction } from '../lib/agent/types';
import { summarizeAction } from '../lib/agent/actions';
import { diffForAction } from '../lib/agent/actionDiff';
import {
  getAutonomy,
  getStreak,
  resetStreak,
  setAutonomy,
  PROMOTION_THRESHOLD,
} from '../lib/agent/autonomy';
import type { ChatMessage } from '../lib/agent/useAgentConversation';

export function MessageBubble({
  message: m,
  people,
  brainFacts,
  canMutate,
  onConfirm,
  onDismiss,
  onRevert,
  onConfirmAll,
  onDismissAll,
  onHandleAll,
}: {
  message: ChatMessage;
  people: Person[];
  brainFacts: BrainFact[];
  canMutate: boolean;
  onConfirm: (messageId: string, proposal: ProposedAction) => void;
  onDismiss: (messageId: string, proposal: ProposedAction) => void;
  onRevert: (messageId: string, proposal: ProposedAction) => void;
  onConfirmAll: (message: ChatMessage) => void;
  onDismissAll: (message: ChatMessage) => void;
  /**
   * Batch affordance for a ranked digest reply: queue EVERY proposed action
   * as fresh pending approval cards at once (routes through propose()). Never
   * runs anything — the operator still confirms each. Optional so surfaces
   * that don't want it can omit it.
   */
  onHandleAll?: (message: ChatMessage) => void;
}) {
  const isMe = m.who === 'me';
  const isArgo = m.who === 'argo';
  const pendingCount = (m.actions ?? []).filter((p) => p.status === 'pending').length;
  // Long action lists collapse to the first 3 cards + an "N more" expander,
  // so a big proposal batch stays scannable without hiding anything for good.
  const [showAllActions, setShowAllActions] = useState(false);
  const allActions = m.actions ?? [];
  const visibleActions =
    showAllActions || allActions.length <= 3 ? allActions : allActions.slice(0, 3);
  const hiddenCount = allActions.length - visibleActions.length;
  return (
    <div style={{ display: 'flex', gap: 12, flexDirection: isMe ? 'row-reverse' : 'row', alignItems: 'flex-end' }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%',
        display: 'inline-grid', placeItems: 'center',
        background: isArgo ? 'linear-gradient(135deg, #FF00FF, #BB4198)' : 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.12)', color: '#fff', flexShrink: 0,
      }}>
        {isArgo ? <Bot size={14} /> : <User size={14} />}
      </div>
      <div style={{ maxWidth: '74%' }}>
        <GlassPanel variant={isMe ? 'magenta' : 'subtle'} animate={false} style={{ padding: '12px 16px', borderRadius: 18 }}>
          <div className="t-body" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{m.body}</div>
          {m.actions && m.actions.length > 0 && (
            <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
              {onHandleAll && canMutate && allActions.length > 2 && (
                <button
                  className="glass-btn glass-btn--primary"
                  onClick={() => onHandleAll(m)}
                  title="Queue every proposed action as pending approvals — nothing runs until you confirm each"
                  style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <ListChecks size={13} /> Handle all {allActions.length}
                </button>
              )}
              {pendingCount > 1 && canMutate && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                  <span className="t-meta" style={{ marginRight: 'auto' }}>
                    {pendingCount} pending writes
                  </span>
                  <button className="glass-btn" onClick={() => onDismissAll(m)} title="Dismiss every pending card">
                    <X size={13} /> Dismiss all
                  </button>
                  <button
                    className="glass-btn glass-btn--primary"
                    onClick={() => onConfirmAll(m)}
                    title="Confirm and run every pending card, in order"
                  >
                    <Check size={13} /> Confirm all {pendingCount}
                  </button>
                </div>
              )}
              {visibleActions.map((p) => (
                <ActionCard
                  key={p.id}
                  proposal={p}
                  diff={diffForAction(p.action, people, brainFacts)}
                  canMutate={canMutate}
                  onConfirm={() => onConfirm(m.id, p)}
                  onDismiss={() => onDismiss(m.id, p)}
                  onRevert={() => onRevert(m.id, p)}
                  streak={getStreak(p.action.kind)}
                  autonomyLevel={getAutonomy(p.action.kind)}
                />
              ))}
              {hiddenCount > 0 && (
                <button
                  className="glass-btn"
                  onClick={() => setShowAllActions(true)}
                  title="Show every proposed action"
                  style={{ justifySelf: 'start', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  <ChevronDown size={13} /> {hiddenCount} more action{hiddenCount === 1 ? '' : 's'}
                </button>
              )}
            </div>
          )}
        </GlassPanel>
        <div className="t-meta" style={{ marginTop: 4, textAlign: isMe ? 'right' : 'left' }}>
          {isArgo ? 'Argo AI' : 'You'} · {m.when}
        </div>
      </div>
    </div>
  );
}

function ActionCard({
  proposal,
  diff,
  canMutate,
  onConfirm,
  onDismiss,
  onRevert,
  streak,
  autonomyLevel,
}: {
  proposal: ProposedAction;
  /** Old → new field change for update actions, null for creates. */
  diff?: string | null;
  canMutate: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
  onRevert: () => void;
  streak?: number;
  autonomyLevel?: 'propose' | 'auto';
}) {
  const { verb, detail } = summarizeAction(proposal.action);
  const done = proposal.status === 'confirmed';
  const reverted = proposal.status === 'reverted';
  const dismissed = proposal.status === 'dismissed';
  const errored = proposal.status === 'error';
  const isAutoRan = done && autonomyLevel === 'auto';
  const accent = errored ? C.orangeWarm : reverted ? C.textDim : isAutoRan ? C.blueElectric : done ? C.green : C.magentaSoft;

  // Show promotion suggestion if streak hit threshold and still in 'propose' mode.
  const showPromotionChip =
    canMutate &&
    proposal.status === 'pending' &&
    streak != null &&
    streak >= PROMOTION_THRESHOLD &&
    autonomyLevel === 'propose';

  return (
    <div
      style={{
        borderRadius: 12,
        border: '1px solid rgba(255,255,255,0.10)',
        background: 'rgba(0,0,0,0.18)',
        padding: '10px 12px',
        opacity: dismissed ? 0.55 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span className="t-card-title" style={{ display: 'block', color: accent, fontWeight: 700 }}>
            {verb}
          </span>
          <span className="t-meta" style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {detail}
          </span>
          {diff && proposal.status === 'pending' && (
            <span
              className="t-meta"
              style={{
                display: 'inline-block',
                marginTop: 4,
                padding: '2px 8px',
                borderRadius: 8,
                background: 'rgba(255,0,255,0.08)',
                border: '1px solid rgba(255,0,255,0.18)',
                fontVariantNumeric: 'tabular-nums',
              }}
              title="Exactly what changes if you confirm"
            >
              {diff}
            </span>
          )}
          {showPromotionChip && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 8,
                padding: '6px 10px',
                borderRadius: 8,
                background: 'rgba(76,209,223,0.06)',
                border: `1px solid rgba(76,209,223,0.22)`,
              }}
            >
              <span className="t-meta" style={{ fontSize: 11, flex: 1 }}>
                Argo has been right {streak} straight times on {verb.toLowerCase()} — auto next time?
              </span>
              <button
                className="glass-btn"
                style={{ padding: '2px 8px', fontSize: 11, flexShrink: 0 }}
                onClick={() => setAutonomy(proposal.action.kind, 'auto')}
                title="Enable auto-execute for this action kind"
              >
                Enable auto
              </button>
              <button
                className="glass-btn"
                style={{ padding: '2px 8px', fontSize: 11, flexShrink: 0 }}
                onClick={() => resetStreak(proposal.action.kind)}
                title="Dismiss this suggestion"
              >
                Keep asking
              </button>
            </div>
          )}
        </span>
        {proposal.status === 'pending' && canMutate && (
          <span style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            <button className="glass-btn" onClick={onDismiss} title="Dismiss">
              <X size={13} />
            </button>
            <button className="glass-btn glass-btn--primary" onClick={onConfirm} title="Confirm and run">
              <Check size={13} /> Confirm
            </button>
          </span>
        )}
        {proposal.status === 'pending' && !canMutate && (
          <span
            className="glass-pill"
            style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}
            title="Read-only access — only editors can run this"
          >
            <Lock size={12} /> Pending
          </span>
        )}
        {done && proposal.undo && (
          <span style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            <span
              className={isAutoRan ? 'glass-pill glass-pill--blue' : 'glass-pill glass-pill--green'}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
            >
              <Check size={12} /> {isAutoRan ? 'Auto-ran' : 'Done'}
            </span>
            {canMutate && (
              <button
                className="glass-btn"
                onClick={onRevert}
                title="Undo this action"
                style={{ padding: '4px 8px', display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <RotateCcw size={12} />
              </button>
            )}
          </span>
        )}
        {done && !proposal.undo && (
          <span
            className={isAutoRan ? 'glass-pill glass-pill--blue' : 'glass-pill glass-pill--green'}
            style={{ flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 4 }}
          >
            <Check size={12} /> {isAutoRan ? 'Auto-ran' : 'Done'}
          </span>
        )}
        {reverted && <span className="glass-pill" style={{ flexShrink: 0 }}>Reverted</span>}
        {dismissed && <span className="glass-pill" style={{ flexShrink: 0 }}>Dismissed</span>}
        {errored && <span className="glass-pill glass-pill--orange" style={{ flexShrink: 0 }}>Failed</span>}
      </div>
      {(done || errored) && proposal.result && proposal.result !== '…' && (
        <div className="t-meta" style={{ marginTop: 8, color: errored ? C.orangeWarm : C.textMuted }}>
          {proposal.result}
        </div>
      )}
    </div>
  );
}
